import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { crmApi } from '../../api/crm.js';
import ActivityFeed from '../../components/ActivityFeed.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import DiscussThread from '../../plus/messenger/DiscussThread.jsx';

export default function Deals() {
  const { id } = useParams();
  if (id === 'new') return <NewDeal />;
  if (id) return <DealDetail id={id} />;
  return <DealList />;
}

function DealList() {
  const [items, setItems] = useState([]);
  useEffect(() => { crmApi.deals().then((d) => setItems(d?.items || [])); }, []);
  return (
    <section>
      <header className="page-header">
        <h1>Deals</h1>
        <Link to="/crm/deals/new" className="btn primary">+ New Deal</Link>
      </header>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Stage</th><th>Value</th><th>Owner</th><th>Status</th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td><Link to={`/crm/deals/${d.id}`}>{d.name}</Link></td>
                <td><span className="chip">{d.stage}</span></td>
                <td>{d.currency} {Number(d.value).toLocaleString()}</td>
                <td className="muted">{d.owner_name || '—'}</td>
                <td><span className={`chip status-${d.status === 'open' ? 'pending' : d.status === 'won' ? 'approved' : 'cancelled'}`}>{d.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No deals yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NewDeal() {
  const nav = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pipelineId, setPipelineId] = useState('');
  const [stages, setStages] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    crmApi.pipelines().then((d) => {
      const list = d?.items || [];
      setPipelines(list);
      const def = list.find((p) => p.is_default) || list[0];
      if (def) {
        setPipelineId(def.id);
        setStages(def.stages || []);
      }
    });
    crmApi.companies().then((d) => setCompanies(d?.items || []));
    crmApi.contacts().then((d) => setContacts(d?.items || []));
  }, []);

  function pickPipeline(id) {
    setPipelineId(id);
    const p = pipelines.find((x) => x.id === id);
    setStages(p?.stages || []);
  }

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      const created = await crmApi.createDeal({
        pipelineId,
        name: fd.get('name'),
        value: Number(fd.get('value') || 0),
        stage: fd.get('stage'),
        contactId: fd.get('contactId') || null,
        companyId: fd.get('companyId') || null,
        expectedClose: fd.get('expectedClose') || null,
      });
      nav(`/crm/deals/${created.deal.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header"><h1>New Deal</h1></header>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={submit} className="card form-card grid-form">
        <label>Pipeline
          <select value={pipelineId} onChange={(e) => pickPipeline(e.target.value)} required>
            {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Stage
          <select name="stage" required>
            {stages.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.probability}%)</option>)}
          </select>
        </label>
        <label className="full">Name<input name="name" required /></label>
        <label>Value<input name="value" type="number" step="0.01" defaultValue={0} /></label>
        <label>Expected close<input name="expectedClose" type="date" /></label>
        <label>Company
          <select name="companyId">
            <option value="">—</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>Contact
          <select name="contactId">
            <option value="">—</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
      </form>
    </section>
  );
}

function DealDetail({ id }) {
  const nav = useNavigate();
  const { can } = useAuth();
  const [deal, setDeal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState(null);

  function reload() {
    crmApi.getDeal(id).then((d) => setDeal(d?.deal));
    crmApi.dealActivities(id).then((d) => setActivities(d?.items || []));
    crmApi.dealQuotations(id).then((d) => setQuotations(d?.items || []));
    crmApi.dealContracts(id).then((d) => setContracts(d?.items || []));
  }
  useEffect(reload, [id]);

  async function logNote(e) {
    e.preventDefault();
    const text = e.currentTarget.elements.note.value;
    if (!text) return;
    await crmApi.logDealActivity(id, { type: 'note', description: text });
    e.currentTarget.reset();
    reload();
  }

  async function newQuote() {
    const lines = window.prompt('Paste quote items as JSON: [{description,quantity,unitPrice,discount,taxRate}, …]');
    if (!lines) return;
    try {
      await crmApi.createQuotation({ dealId: id, items: JSON.parse(lines), validUntil: null });
      reload();
    } catch (err) { setError('Bad JSON'); }
  }
  async function sendQuote(qid) { await crmApi.sendQuotation(qid); reload(); }
  async function genQuotePdf(qid) { const r = await crmApi.generateQuotePdf(qid); window.open(r.url, '_blank'); reload(); }

  async function newContract() {
    const no = window.prompt('Contract number?'); if (!no) return;
    const start = new Date().toISOString().slice(0, 10);
    await crmApi.createContract({ dealId: id, contractNo: no, value: deal.value, startDate: start });
    reload();
  }
  async function signContract(cid) { await crmApi.signContract(cid); reload(); }

  async function convert() {
    const wsId = window.prompt('Workspace UUID for the new project?');
    if (!wsId) return;
    const key = window.prompt('Project key (UPPERCASE, e.g. ACME)?');
    if (!key) return;
    try {
      const r = await crmApi.convertDealToProject(id, { workspaceId: wsId, projectKey: key.toUpperCase() });
      nav(`/pm/projects/${r.project.key}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Convert failed');
    }
  }

  if (!deal) return <div className="muted">Loading…</div>;
  const stages = typeof deal.pipeline_stages === 'string' ? JSON.parse(deal.pipeline_stages) : deal.pipeline_stages || [];

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{deal.name}</h1>
          <p className="muted">{deal.company_name || '—'} · {deal.contact_name || 'no contact'} · <span className="chip">{deal.stage}</span> · <span className={`chip status-${deal.status === 'open' ? 'pending' : deal.status === 'won' ? 'approved' : 'cancelled'}`}>{deal.status}</span></p>
        </div>
        <div className="actions">
          <Link to="/crm/pipeline" className="btn ghost">← Board</Link>
          {deal.status === 'won' && !deal.converted_project_id && <button className="btn primary" onClick={convert}>Convert to Project</button>}
          {deal.converted_project_id && <span className="chip status-approved">Project linked</span>}
        </div>
      </header>
      {error && <div className="alert error">{error}</div>}

      <div className="tabs">
        <button className={tab === 'overview' ? 'tab active' : 'tab'} onClick={() => setTab('overview')}>Overview</button>
        <button className={tab === 'activities' ? 'tab active' : 'tab'} onClick={() => setTab('activities')}>Activities</button>
        <button className={tab === 'quotes' ? 'tab active' : 'tab'} onClick={() => setTab('quotes')}>Quotations ({quotations.length})</button>
        <button className={tab === 'contracts' ? 'tab active' : 'tab'} onClick={() => setTab('contracts')}>Contracts ({contracts.length})</button>
      </div>

      <div className="grid-2">
        <div>
          {tab === 'overview' && (
            <div className="card">
              <h3>Stage progress</h3>
              <div className="stage-bar">
                {stages.map((s) => (
                  <div key={s.name} className={`stage-step ${deal.stage === s.name ? 'active' : ''} ${stages.findIndex((x) => x.name === deal.stage) >= stages.findIndex((x) => x.name === s.name) ? 'done' : ''}`}>
                    <div className="dot" />
                    <span>{s.name}</span>
                  </div>
                ))}
              </div>
              <dl className="kv">
                <dt>Value</dt><dd>{deal.currency} {Number(deal.value).toLocaleString()}</dd>
                <dt>Owner</dt><dd>{deal.owner_name || '—'}</dd>
                <dt>Expected close</dt><dd>{deal.expected_close || '—'}</dd>
                <dt>Contact</dt><dd>{deal.contact_name || '—'} · <span className="muted small">{deal.contact_email}</span></dd>
              </dl>
            </div>
          )}

          {tab === 'activities' && (
            <div className="card">
              <h3>Activity log</h3>
              <form onSubmit={logNote} className="row-form" style={{ gridTemplateColumns: '1fr 100px' }}>
                <input name="note" placeholder="Add a note…" required />
                <button className="btn primary" type="submit">Log</button>
              </form>
              <ul className="comment-list">
                {activities.map((a) => (
                  <li key={a.id}>
                    <header>
                      <span className={`chip type-${a.type}`}>{a.type}</span>
                      <strong>{a.user_name || '—'}</strong>
                      <span className="muted small">{new Date(a.created_at).toLocaleString()}</span>
                    </header>
                    <p>{a.description}</p>
                  </li>
                ))}
                {activities.length === 0 && <li className="muted">No activities yet.</li>}
              </ul>
            </div>
          )}

          {tab === 'quotes' && (
            <div className="card">
              <header className="page-header"><h3>Quotations</h3><button className="btn primary small" onClick={newQuote}>+ New Quote</button></header>
              <table className="data-table">
                <thead><tr><th>#</th><th>Total</th><th>Status</th><th>Valid Until</th><th></th></tr></thead>
                <tbody>
                  {quotations.map((q) => (
                    <tr key={q.id}>
                      <td><code>{q.quote_no}</code></td>
                      <td>{q.currency} {Number(q.total).toFixed(2)}</td>
                      <td><span className={`chip status-${q.status === 'sent' ? 'approved' : 'pending'}`}>{q.status}</span></td>
                      <td>{q.valid_until || 'open'}</td>
                      <td>
                        {q.pdf_url ? <a href={q.pdf_url} target="_blank" rel="noreferrer" className="btn ghost small">PDF</a>
                          : <button className="btn ghost small" onClick={() => genQuotePdf(q.id)}>Generate PDF</button>}
                        {q.status === 'draft' && <button className="btn primary small" onClick={() => sendQuote(q.id)}>Send</button>}
                      </td>
                    </tr>
                  ))}
                  {quotations.length === 0 && <tr><td colSpan={5} className="muted">No quotations.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'contracts' && (
            <div className="card">
              <header className="page-header"><h3>Contracts</h3><button className="btn primary small" onClick={newContract}>+ New Contract</button></header>
              <table className="data-table">
                <thead><tr><th>#</th><th>Value</th><th>Period</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id}>
                      <td><code>{c.contract_no}</code></td>
                      <td>{c.currency} {Number(c.value).toFixed(2)}</td>
                      <td>{c.start_date} → {c.end_date || 'open'}</td>
                      <td><span className={`chip status-${c.status === 'signed' ? 'approved' : 'pending'}`}>{c.status}</span></td>
                      <td>{c.status !== 'signed' && <button className="btn primary small" onClick={() => signContract(c.id)}>Mark Signed</button>}</td>
                    </tr>
                  ))}
                  {contracts.length === 0 && <tr><td colSpan={5} className="muted">No contracts.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside>
          <div className="card">
            <ActivityFeed resourceType="deal" resourceId={id} title="Activity feed" />
          </div>
          {can('messenger_thread', 'read') && (
            <div className="card" data-testid="deal-discussion">
              <h3>Discussion</h3>
              <DiscussThread subjectType="deal" subjectId={id} subjectTitle={deal?.name || 'Deal'} shareUrl={`/crm/deals/${id}`} />
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
